import { ApiError, apiErrorCode } from "@/lib/api";
import {
  messageFor,
  VALIDATION_SUMMARY,
  validationFailures,
  validationReasonCopy,
  type ValidationReason,
} from "@/lib/errors";
import type {
  WizardFieldErrors,
  WizardReasonWording,
  WizardRefusalWording,
} from "@/base/wizard/types";

// What a refused save or submit means for the wizard (BRK-S300). It keeps
// anchorServerError's shape — one reason per field plus one summary line — but
// not its module: that one reads the bond flow's own refusals (owners, vendor
// paths, the final step) and imports surety code, which the wizard never does
// (test/lib/submissions-boundary.test.ts). The commercial routes refuse a save
// in two shapes, both read here with the shared helpers in src/lib/errors.ts:
//   • a validation 400 with `details.fieldErrors`, dotted path → message
//     (PMS lib/api/client-error.ts), `_root` naming the whole value; a 422
//     that names its fields the same way reads alike;
//   • `VALIDATION_FAILED` with `details.fields[{path, reason}]`, whose closed
//     reasons are shown in the step's words for that path (BRK-S339), else
//     the portal's (BRK-S279).
// A bound submission refuses every write with a 409 `submission_locked`
// (PMS-S708). Retrying can't change that, so it reads apart (BRK-S305), with
// PMS's own sentence for why: a bind request locks it too after the MVP
// (PMS-S663), and must not read as "bound" (BRK-S307).

export type SaveError =
  | { kind: "fields"; byField: WizardFieldErrors; summary: string }
  /** The request never reached PMS: nothing was saved, so Retry is safe. */
  | { kind: "network"; message: string }
  | { kind: "refused"; message: string }
  /** The submission is locked (bound): nothing about its application can
   *  change. `message` is the reason. */
  | { kind: "locked"; message: string };

export const NETWORK_MESSAGE =
  "We couldn’t reach Signal. Check your connection, then try again.";

/** Why a locked submission can't change, when PMS gave no sentence of its
 *  own. */
export const LOCKED_MESSAGE =
  "This submission is bound, so its application can’t change.";

/** The key PMS gives a message about the whole value rather than one field;
 *  a step's own `validate` uses it the same way (BRK-S301). */
export const ROOT = "_root";

function fieldErrorsOf(err: ApiError): Record<string, string> | null {
  const body = err.body;
  if (typeof body !== "object" || body === null) return null;
  const details = (body as { details?: unknown }).details;
  if (typeof details !== "object" || details === null) return null;
  const map = (details as { fieldErrors?: unknown }).fieldErrors;
  if (typeof map !== "object" || map === null || Array.isArray(map))
    return null;
  const out: Record<string, string> = {};
  for (const [path, message] of Object.entries(map)) {
    if (typeof message === "string" && message.trim()) out[path] = message;
  }
  return Object.keys(out).length > 0 ? out : null;
}

/** The sentence a coded refusal carries in its envelope's `error`, if any:
 *  one PMS wrote for the broker (src/lib/errors.ts). */
function ownSentence(err: ApiError): string | null {
  const body = err.body;
  if (typeof body !== "object" || body === null) return null;
  const said = (body as { error?: unknown }).error;
  return typeof said === "string" && said.trim() ? said : null;
}

/** The words for a text field: what to enter, for an empty or wrong value,
 *  and to shorten one too long. */
export function textWords(enter: string, field: string): WizardReasonWording {
  return {
    required: enter,
    too_short: enter,
    too_long: `Shorten the ${field}.`,
    invalid: enter,
  };
}

/** The words for a field whose every refusal reads as its one sentence: a
 *  choice, a date, or a number whose sentence names its range. */
export function oneWords(sentence: string): WizardReasonWording {
  return {
    required: sentence,
    too_short: sentence,
    too_long: sentence,
    invalid: sentence,
  };
}

/** The step's sentence for a refused path, `*` matching a row's index. */
function stepWords(
  wording: WizardRefusalWording | undefined,
  path: string,
  reason: ValidationReason,
): string | undefined {
  if (!wording) return undefined;
  if (Object.hasOwn(wording, path)) return wording[path][reason];
  const parts = path.split(".");
  for (const [pattern, words] of Object.entries(wording)) {
    const want = pattern.split(".");
    if (
      want.length === parts.length &&
      want.every(
        (p, i) => p === parts[i] || (p === "*" && /^\d+$/.test(parts[i])),
      )
    )
      return words[reason];
  }
  return undefined;
}

/** A refused save or submit, read for the wizard; `wording` is the step's
 *  own for what PMS's request checks refuse (BRK-S339). */
export function readSaveError(
  err: unknown,
  wording?: WizardRefusalWording,
): SaveError {
  if (err instanceof ApiError && err.status === 0) {
    return { kind: "network", message: NETWORK_MESSAGE };
  }
  if (err instanceof ApiError && apiErrorCode(err) === "submission_locked") {
    return { kind: "locked", message: ownSentence(err) ?? LOCKED_MESSAGE };
  }
  // A 422 that names its fields is read as a 400 is: a write well formed but
  // wrong, such as a prior coverage row (PMS-S810) or limits PMS doesn't
  // write (PMS-S811).
  if (err instanceof ApiError && (err.status === 400 || err.status === 422)) {
    const map = fieldErrorsOf(err);
    if (map) {
      const { [ROOT]: root, ...byField } = map;
      return { kind: "fields", byField, summary: root ?? VALIDATION_SUMMARY };
    }
    const failures = validationFailures(err);
    if (failures !== null) {
      const byField: Record<string, string> = {};
      for (const { path, reason } of failures) {
        byField[path] ??=
          stepWords(wording, path, reason) ?? validationReasonCopy(reason);
      }
      return { kind: "fields", byField, summary: VALIDATION_SUMMARY };
    }
  }
  return { kind: "refused", message: messageFor(err) };
}
